import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Text, Flex, Table, Thead, Tbody, Tr, Th, Td, Select, HStack, Tag, TagLabel, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay, useDisclosure, Input, Spacer
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';

import { useProjectContext } from '../context/ProjectContext';
import useAnalyticsAPI from '../hooks/useAnalyticsAPI';

// Minimal ADF -> HTML fallback renderer
function adfToHtml(node) {
  if (!node) return '';
  if (typeof node === 'string') return node;
  const walk = (n) => {
    if (!n) return '';
    const t = n.type;
    const getText = (arr = []) => arr.map(walk).join('');
    switch (t) {
      case 'doc':
        return getText(n.content);
      case 'paragraph':
        return `<p>${getText(n.content)}</p>`;
      case 'text': {
        let txt = n.text || '';
        if (Array.isArray(n.marks)) {
          n.marks.forEach(m => {
            if (m.type === 'strong') txt = `<strong>${txt}</strong>`;
            else if (m.type === 'em') txt = `<em>${txt}</em>`;
            else if (m.type === 'code') txt = `<code>${txt}</code>`;
            else if (m.type === 'link' && m.attrs?.href) txt = `<a href="${m.attrs.href}" target="_blank" rel="noreferrer">${txt}</a>`;
          });
        }
        return txt;
      }
      case 'heading': {
        const lvl = n.attrs?.level || 3;
        return `<h${lvl}>${getText(n.content)}</h${lvl}>`;
      }
      case 'bulletList':
        return `<ul>${getText(n.content)}</ul>`;
      case 'orderedList':
        return `<ol>${getText(n.content)}</ol>`;
      case 'listItem':
        return `<li>${getText(n.content)}</li>`;
      case 'panel':
        return `<div class="adf-panel">${getText(n.content)}</div>`;
      case 'rule':
        return '<hr />';
      case 'mediaSingle':
      case 'media':
        return '';
      case 'taskList':
        return `<ul class="tasklist">${getText(n.content)}</ul>`;
      case 'taskItem':
        return `<li>${getText(n.content)}</li>`;
      case 'hardBreak':
        return '<br />';
      default:
        return getText(n.content);
    }
  };
  return walk(node);
}

export default function BugTicketsPage() {
  const { effectiveProjectParam } = useProjectContext();
  const { makeRequest } = useAnalyticsAPI();

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [assignee, setAssignee] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Data
  const [issues, setIssues] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1, hasNext: false, hasPrev: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedIssue, setSelectedIssue] = useState(null);

  const getPriorityColor = (p) => {
    if (!p) return 'gray';
    const v = String(p).toLowerCase();
    if (v.includes('highest')) return 'red';
    if (v.includes('high')) return 'orange';
    if (v.includes('medium')) return 'yellow';
    if (v.includes('low')) return 'green';
    return 'gray';
  };

  const fetchBugs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(limit));
      if (effectiveProjectParam) params.append('project', effectiveProjectParam);
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      if (priority) params.append('priority', priority);
      if (assignee) params.append('assignee', assignee);
      params.append('sort', '-jiraUpdated');
      params.append('include', 'descriptionHtml,description');
      
      const endpoint = `/api/bugs?${params.toString()}`;
      const data = await makeRequest(endpoint);
      
      setIssues(Array.isArray(data?.issues) ? data.issues : []);
      if (data?.pagination) setPagination(data.pagination);
    } catch (e) {
      setError(e.message || 'Failed to load tickets');
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, effectiveProjectParam, search, status, priority, assignee, makeRequest]);

  useEffect(() => { fetchBugs(); }, [fetchBugs]);

  // Fetch full issue when opening if we lack description
  const openDetails = async (issue) => {
    setSelectedIssue(issue);
    onOpen();
    if (!issue?.descriptionHtml && !issue?.description) {
      try {
        const endpoint = `/api/bugs/${encodeURIComponent(issue.issueKey)}?include=descriptionHtml,description`;
        const full = await makeRequest(endpoint);
        if (full && (full.descriptionHtml || full.description)) {
          setSelectedIssue(prev => ({ ...prev, descriptionHtml: full.descriptionHtml, description: full.description }));
        }
      } catch (_) { /* ignore */ }
    }
  };

  const resetFilters = () => {
    setSearch(''); setStatus(''); setPriority(''); setAssignee(''); setPage(1);
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    params.append('page', '1'); 
    params.append('limit', String(limit));
    if (effectiveProjectParam) params.append('project', effectiveProjectParam);
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);
    if (assignee) params.append('assignee', assignee);
    params.append('format', 'csv');
    
    try {
      const endpoint = `/api/bugs/export?${params.toString()}`;
      // For file downloads, we still need to use window.open with the proper authenticated URL
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const API_KEY = process.env.REACT_APP_API_KEY;
      const authenticatedUrl = `${API_BASE_URL}${endpoint}&api_key=${encodeURIComponent(API_KEY)}`;
      window.open(authenticatedUrl, '_blank');
    } catch (e) {
      console.error('Failed to export CSV:', e);
    }
  };

  const descriptionHtml = useMemo(() => {
    if (selectedIssue?.descriptionHtml) return selectedIssue.descriptionHtml;
    if (selectedIssue?.description) {
      try {
        const adf = typeof selectedIssue.description === 'string' ? JSON.parse(selectedIssue.description) : selectedIssue.description;
        return adfToHtml(adf);
      } catch (_) {
        return selectedIssue.description;
      }
    }
    return '';
  }, [selectedIssue]);

  const statusOptions = useMemo(() => {
    const statuses = [...new Set(issues.map(i => i.status).filter(Boolean))];
    return statuses.sort();
  }, [issues]);

  const priorityOptions = useMemo(() => {
    const priorities = [...new Set(issues.map(i => i.priority).filter(Boolean))];
    return priorities.sort();
  }, [issues]);



  const hasNext = pagination.hasNext;
  const hasPrev = pagination.hasPrev;

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={fetchBugs} />;

  return (
    <Page title="Bug Tickets">
      <SectionCard>
        <Flex direction="column" gap={4}>
          {/* Filters */}
          <Flex gap={4} wrap="wrap" align="center">
            <Input
              placeholder="Search summaries, keys, description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxW="300px"
            />
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              placeholder="All Status"
              maxW="150px"
            >
              {statusOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              placeholder="All Priority"
              maxW="150px"
            >
              {priorityOptions.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </Select>
            <Input
              placeholder="Assignee"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              maxW="150px"
            />
            <Spacer />
            <Button
              leftIcon={<RepeatIcon />}
              onClick={resetFilters}
              variant="outline"
              size="sm"
            >
              Reset
            </Button>
            <Button
              onClick={exportCsv}
              colorScheme="blue"
              size="sm"
            >
              Export CSV
            </Button>
          </Flex>

          {/* Table */}
          {issues.length === 0 ? (
            <EmptyState message="No tickets found" />
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>KEY</Th>
                    <Th>SUMMARY</Th>
                    <Th>STATUS</Th>
                    <Th>PRIORITY</Th>
                    <Th>ASSIGNEE</Th>
                    <Th>UPDATED</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {issues.map((issue) => (
                    <Tr
                      key={issue.issueKey}
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                      onClick={() => openDetails(issue)}
                    >
                      <Td fontWeight="bold">{issue.issueKey}</Td>
                      <Td maxW="300px" isTruncated>{issue.summary}</Td>
                      <Td>
                        <Tag size="sm" colorScheme={issue.status === 'Done' ? 'green' : issue.status === 'In Progress' ? 'blue' : 'yellow'}>
                          <TagLabel>{issue.status}</TagLabel>
                        </Tag>
                      </Td>
                      <Td>
                        <Tag size="sm" colorScheme={getPriorityColor(issue.priority)}>
                          <TagLabel>{issue.priority}</TagLabel>
                        </Tag>
                      </Td>
                      <Td>{issue.assignee || '-'}</Td>
                      <Td>{issue.jiraUpdated ? new Date(issue.jiraUpdated).toLocaleDateString() : '-'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          {/* Pagination */}
          {issues.length > 0 && (
            <Flex justify="space-between" align="center" mt={4}>
              <Text fontSize="sm" color="gray.600">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total} tickets
              </Text>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  isDisabled={!hasPrev}
                >
                  Previous
                </Button>
                <Text fontSize="sm">Page {page} of {pagination.totalPages}</Text>
                <Button
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  isDisabled={!hasNext}
                >
                  Next
                </Button>
              </HStack>
            </Flex>
          )}
        </Flex>
      </SectionCard>

      {/* Issue Details Drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{selectedIssue?.issueKey}</DrawerHeader>
          <DrawerBody>
            {selectedIssue && (
              <Box>
                <Text fontWeight="bold" mb={2}>{selectedIssue.summary}</Text>
                <HStack mb={4} spacing={4}>
                  <Tag colorScheme={selectedIssue.status === 'Done' ? 'green' : selectedIssue.status === 'In Progress' ? 'blue' : 'yellow'}>
                    {selectedIssue.status}
                  </Tag>
                  <Tag colorScheme={getPriorityColor(selectedIssue.priority)}>
                    {selectedIssue.priority}
                  </Tag>
                  {selectedIssue.assignee && (
                    <Tag colorScheme="blue">{selectedIssue.assignee}</Tag>
                  )}
                </HStack>
                {descriptionHtml && (
                  <Box
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                    sx={{
                      'h1, h2, h3, h4, h5, h6': { fontWeight: 'bold', mb: 2 },
                      'p': { mb: 2 },
                      'ul, ol': { ml: 4, mb: 2 },
                      'li': { mb: 1 },
                      'code': { bg: 'gray.100', px: 1, borderRadius: 'sm' },
                      'pre': { bg: 'gray.100', p: 2, borderRadius: 'md', overflow: 'auto' },
                      'a': { color: 'blue.500', textDecoration: 'underline' },
                      'strong': { fontWeight: 'bold' },
                      'em': { fontStyle: 'italic' }
                    }}
                  />
                )}
              </Box>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Page>
  );
}
